import { useCallback, useContext, useRef, useEffect } from "react";
import useHttp from "../../../../hooks/useHttp";
import { AppContext } from "../../../../context/AppContext";

const SAVED_FILTERS_PAGE_SIZE = 100;

export const useSavedFilters = () => {
    const { fetchData } = useHttp();
    const [appProperties] = useContext(AppContext);
    // Ref keeps the latest appProperties available inside useCallback without adding it as a dependency,
    // preventing re-creation of the memoized API functions when appProperties changes.
    const appPropertiesRef = useRef(appProperties);
    useEffect(() => {
        appPropertiesRef.current = appProperties;
    }, [appProperties]);

    const getSavedFilters = useCallback(
        async (start = 0, limit = SAVED_FILTERS_PAGE_SIZE, search = "") => {
            let queryParams = `saved-filters?start=${start}&limit=${limit}`;
            if (search?.trim()) {
                queryParams += `&search=${encodeURIComponent(search.trim())}`;
            }
            const response = await fetchData(queryParams, "GET", null, appPropertiesRef.current);
            if (response) {
                // Response shape: stringified axios response -> { data: SMSResponse { data, paginationModel } }
                const axiosResponse = JSON.parse(response);
                const smsResponse = axiosResponse?.data;
                const rawData = smsResponse?.data;
                return {
                    data: Array.isArray(rawData) ? rawData : [],
                    cursor: smsResponse?.paginationModel?.cursor ?? null,
                };
            }
            return { data: [], cursor: null };
        },
        [fetchData]
    );

    const createFilter = useCallback(
        async (filter) => {
            const response = await fetchData("saved-filters", "POST", filter, appPropertiesRef.current);
            if (!response) {
                throw new Error("Failed to save view");
            }
            const axiosResponse = JSON.parse(response);
            const body = axiosResponse?.data;
            if (body?.success === false) {
                throw new Error(body?.errorMessage ?? "Failed to save view");
            }
            return body?.data;
        },
        [fetchData]
    );

    const updateFilter = useCallback(
        async (filterId, payload) => {
            const response = await fetchData(`saved-filters/${filterId}`, "PUT", payload, appPropertiesRef.current);
            if (!response) {
                throw new Error("Failed to update view");
            }
            const axiosResponse = JSON.parse(response);
            const body = axiosResponse?.data;
            if (body?.success === false) {
                throw new Error(body?.errorMessage ?? "Failed to update view");
            }
            return body?.data;
        },
        [fetchData]
    );

    const deleteFilter = useCallback(
        async (filterId) => {
            const response = await fetchData(
                `saved-filters/${filterId}`,
                "DELETE",
                null,
                appPropertiesRef.current
            );
            if (!response) {
                throw new Error("Failed to delete view");
            }
            const responseData = JSON.parse(response);
            if (responseData?.success === false) {
                throw new Error(responseData?.errorMessage ?? "Failed to delete view");
            }
            return responseData;
        },
        [fetchData]
    );

    return {
        getSavedFilters,
        createFilter,
        updateFilter,
        deleteFilter,
    };
};
